import {create} from 'zustand';
import {useShallow} from 'zustand/react/shallow';
import {SafetyRecommendationStatusGroup} from '../@types/safety-recommendation';
import {PersistedChatMessage} from './irp-workbook.store';
import {AgentTurn} from '../components/common/agent/AgentChatPanel';

/**
 * What the Safety Recommendations register is showing. Every field is sent to the server on each
 * search, so the register is never filtered in the browser: the whole local copy is too large.
 */
export interface RegisterFilters {
    text: string;
    /** Focus status groups (the FAA's own where it is an addressee); empty = every group. */
    statusGroups: SafetyRecommendationStatusGroup[];
    addressedTo: 'ANY' | 'FAA' | 'NOT_FAA';
    issuedFromYear: number | null;
    issuedToYear: number | null;
    includeSuperseded: boolean;
    /** Taxonomy classification ids; empty = every classification. */
    taxonomyLeafIds: number[];
    /** Source codes; empty = every source. */
    sources: string[];
    /**
     * 'default' means relevance while there is search text and issue date otherwise. Anything else
     * is a column the user picked, which must then be honoured whether or not they are searching.
     */
    sortBy: 'default' | 'issuedDate' | 'number' | 'daysOpen';
    sortDirection: 'asc' | 'desc';
    page: number;
}

export const DEFAULT_REGISTER_FILTERS: RegisterFilters = {
    text: '',
    statusGroups: [],
    addressedTo: 'ANY',
    issuedFromYear: null,
    issuedToYear: null,
    includeSuperseded: false,
    taxonomyLeafIds: [],
    sources: [],
    sortBy: 'default',
    sortDirection: 'desc',
    page: 0,
};

interface RegisterStore {
    filters: RegisterFilters;
    /** The register's conversation with the agent, kept so closing the panel loses nothing. */
    agentTurns: AgentTurn[];
    /** Each open recommendation's own chat, by recommendation id. */
    detailChatHistory: Map<number, PersistedChatMessage[]>;
    detailChatContextSent: Map<number, boolean>;

    /**
     * Changes some filters. Anything but a page turn goes back to the first page, so a narrowed
     * search can never open on a page that no longer exists.
     */
    setFilters: (changes: Partial<RegisterFilters>) => void;
    resetFilters: () => void;
    setAgentTurns: (turns: AgentTurn[]) => void;
    setDetailChatHistory: (recommendationId: number, messages: PersistedChatMessage[]) => void;
    setDetailChatContextSent: (recommendationId: number, sent: boolean) => void;
}

/**
 * One register, one store: unlike a workbook there is only ever a single Safety Recommendations
 * register open, so this is a plain store rather than one created per tab.
 */
export const useRegisterStore = create<RegisterStore>((set) => ({
    filters: DEFAULT_REGISTER_FILTERS,
    agentTurns: [],
    detailChatHistory: new Map(),
    detailChatContextSent: new Map(),

    setFilters: (changes: Partial<RegisterFilters>): void => set((state) => {
        const turningThePage = Object.keys(changes).length === 1 && changes.page !== undefined;
        return {filters: {...state.filters, ...changes, page: turningThePage ? changes.page as number : 0}};
    }),
    resetFilters: (): void => set({filters: DEFAULT_REGISTER_FILTERS}),
    setAgentTurns: (turns: AgentTurn[]): void => set({agentTurns: turns}),
    setDetailChatHistory: (recommendationId: number, messages: PersistedChatMessage[]): void => set((state) => {
        const history = new Map(state.detailChatHistory);
        history.set(recommendationId, messages);
        return {detailChatHistory: history};
    }),
    setDetailChatContextSent: (recommendationId: number, sent: boolean): void => set((state) => {
        const sentById = new Map(state.detailChatContextSent);
        sentById.set(recommendationId, sent);
        return {detailChatContextSent: sentById};
    }),
}));

export const useRegisterFilters = (): RegisterFilters => useRegisterStore(useShallow((state) => state.filters));

export const useRegisterAgentTurns = (): AgentTurn[] => useRegisterStore((state) => state.agentTurns);

export const useDetailChatHistory = (recommendationId: number | null): PersistedChatMessage[] => {
    const history = useRegisterStore((state) => state.detailChatHistory);
    return recommendationId === null ? [] : history.get(recommendationId) ?? [];
};

export const useDetailChatContextSent = (recommendationId: number | null): boolean => {
    const sent = useRegisterStore((state) => state.detailChatContextSent);
    return recommendationId === null ? false : sent.get(recommendationId) ?? false;
};
